export default function TermsOfService() {
  return (
    <main className="mx-auto max-w-3xl px-6 py-16 md:py-24 text-foreground">
      <header className="mb-12 border-b border-border pb-8">
        <h1 className="text-4xl md:text-5xl font-semibold tracking-tight mb-3">
          Terms of Service
        </h1>
        <p className="text-base text-muted-foreground">
          Last updated May 2, 2026
        </p>
      </header>

      <section className="mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          Agreement to Our Legal Terms
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            We are LegacyLens AI, Inc., doing business as LegacyLens AI
            ("Company," "we," "us," "our"), a company registered in Delaware,
            United States at 131 Continental Drive, Suite 305, Newark, DE
            19713.
          </p>
          <p>
            We operate the website{" "}
            <a
              href="https://legacylensai.com"
              className="font-medium underline-offset-4 hover:underline"
            >
              https://legacylensai.com
            </a>{" "}
            (the "Site"), as well as any other related products and services
            that refer or link to these legal terms (the "Legal Terms")
            (collectively, the "Services").
          </p>
          <p>
            LegacyLens AI is a digital legacy platform that enables individuals
            (Subjects) to authorize, capture, and preserve their voice,
            likeness, biographical content, and personal narrative as a Digital
            Legacy that can be accessed by their families, descendants, and
            authorized parties during their lifetime and after their death. The
            platform operates under the Digital Legacy Directive
            (legacylensai.com/digital-legacy-directive), a public framework
            that governs how voice, likeness, and memories may be captured,
            preserved, and shared with the Subject's verifiable consent.
            LegacyLens serves Subjects directly, Buyers who commission Digital
            Legacies as gifts to Subjects or their families, and partner
            institutions including cemeteries, funeral homes, and ancestry
            services that host or facilitate access to Digital Legacies on
            behalf of authorized Subjects.
          </p>
          <p>
            You can contact us by phone at (+1) 646-389-1263, email at{" "}
            <a
              href="mailto:legal@legacylensai.com"
              className="font-medium underline-offset-4 hover:underline"
            >
              legal@legacylensai.com
            </a>
            , or by mail to 447 Broadway, 2nd Floor #1240, New York, NY 10013,
            United States.
          </p>
          <p>
            These Legal Terms constitute a legally binding agreement made
            between you, whether personally or on behalf of an entity ("you"),
            and LegacyLens AI, Inc., concerning your access to and use of the
            Services. You agree that by accessing the Services, you have read,
            understood, and agreed to be bound by all of these Legal Terms. IF
            YOU DO NOT AGREE WITH ALL OF THESE LEGAL TERMS, THEN YOU ARE
            EXPRESSLY PROHIBITED FROM USING THE SERVICES AND YOU MUST
            DISCONTINUE USE IMMEDIATELY.
          </p>
          <p>
            We will provide you with prior notice of any scheduled changes to
            the Services you are using. Changes to these Legal Terms will
            become effective thirty (30) days after the notice is given,
            except if the changes apply to security updates, bug fixes, a
            court order, compliance with applicable law (updates necessary to
            comply with applicable law, regulation, or regulatory guidance,
            including but not limited to changes required by GDPR, CCPA, state
            privacy laws, BIPA, CUBI, or other consumer protection or
            biometric privacy statutes), and modifications to ensure platform
            safety and integrity (updates necessary to enforce platform
            safety, prevent abuse, or maintain the integrity of subject
            content as protected under the Digital Legacy Directive, including
            updates to prohibited activities, avatar safety guardrails, or
            content moderation procedures), in which case the changes will be
            effective immediately. By continuing to use the Services after the
            effective date of any changes, you agree to be bound by the
            modified terms. If you disagree with such changes, you may
            terminate Services as per the section "Term and Termination."
          </p>
          <p>
            The Services are intended for users who are at least 18 years old.
            Persons under the age of 18 are not permitted to use or register
            for the Services.
          </p>
          <p>We recommend that you print a copy of these Legal Terms for your records.</p>
        </div>
      </section>

      <section className="mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          Table of Contents
        </h2>
        <ol className="space-y-2 text-lg leading-relaxed list-decimal pl-6">
          <li><a href="#section-1" className="hover:underline">Our Services</a></li>
          <li><a href="#section-2" className="hover:underline">Intellectual Property Rights</a></li>
          <li><a href="#section-3" className="hover:underline">User Representations</a></li>
          <li><a href="#section-4" className="hover:underline">User Registration</a></li>
          <li><a href="#section-5" className="hover:underline">Purchases and Payment</a></li>
          <li><a href="#section-6" className="hover:underline">Prohibited Activities</a></li>
          <li><a href="#section-7" className="hover:underline">User Generated Contributions</a></li>
          <li><a href="#section-8" className="hover:underline">Contribution License</a></li>
          <li><a href="#section-9" className="hover:underline">Third-Party Websites and Content</a></li>
          <li><a href="#section-10" className="hover:underline">Services Management</a></li>
          <li><a href="#section-11" className="hover:underline">Privacy Policy</a></li>
          <li><a href="#section-12" className="hover:underline">Digital Millennium Copyright Act (DMCA) Notice and Policy</a></li>
          <li><a href="#section-13" className="hover:underline">Term and Termination</a></li>
          <li><a href="#section-14" className="hover:underline">Modifications and Interruptions</a></li>
          <li><a href="#section-15" className="hover:underline">Governing Law</a></li>
          <li><a href="#section-16" className="hover:underline">Dispute Resolution</a></li>
          <li><a href="#section-17" className="hover:underline">Corrections</a></li>
          <li><a href="#section-18" className="hover:underline">Disclaimer</a></li>
          <li><a href="#section-19" className="hover:underline">Limitations of Liability</a></li>
          <li><a href="#section-20" className="hover:underline">Indemnification</a></li>
          <li><a href="#section-21" className="hover:underline">User Data</a></li>
          <li><a href="#section-22" className="hover:underline">Electronic Communications, Transactions, and Signatures</a></li>
          <li><a href="#section-23" className="hover:underline">California Users and Residents</a></li>
          <li><a href="#section-24" className="hover:underline">Miscellaneous</a></li>
          <li><a href="#section-25" className="hover:underline">Subject Consent and Authorization</a></li>
          <li><a href="#section-26" className="hover:underline">Ownership, Use, and No Commercial Exploitation</a></li>
          <li><a href="#section-27" className="hover:underline">Portability and Platform Transition</a></li>
          <li><a href="#section-28" className="hover:underline">Contact Us</a></li>
        </ol>
      </section>

      <section id="section-1" className="scroll-mt-24 mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          1. Our Services
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            The information provided when using the Services is not intended
            for distribution to or use by any person or entity in any
            jurisdiction or country where such distribution or use would be
            contrary to law or regulation or which would subject us to any
            registration requirement within such jurisdiction or country.
            Accordingly, those persons who choose to access the Services from
            other locations do so on their own initiative and are solely
            responsible for compliance with local laws, if and to the extent
            local laws are applicable.
          </p>
          <p>
            The Services are not tailored to comply with industry-specific
            regulations (Health Insurance Portability and Accountability Act
            (HIPAA), Federal Information Security Management Act (FISMA),
            etc.), so if your interactions would be subjected to such laws,
            you may not use the Services. You may not use the Services in a
            way that would violate the Gramm-Leach-Bliley Act (GLBA).
          </p>
        </div>

        <h3 className="text-xl font-semibold mt-10 mb-4">Geographic availability</h3>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            The Services are currently offered only to residents of Eligible
            States. For purposes of these Legal Terms, an "Eligible State"
            means any state of the United States other than Illinois, Texas,
            and Washington. The Services are not offered to residents of
            Illinois, Texas, or Washington, nor to any person located outside
            the United States, including but not limited to residents of the
            European Union, the United Kingdom, Canada, and any country
            governed by data protection laws that impose obligations
            LegacyLens has not yet undertaken. We are working toward expanded
            geographic availability and will update these Legal Terms when the
            Services become available in additional jurisdictions. The list of
            Eligible States may change from time to time as our geographic
            availability evolves; the version of these Legal Terms in effect
            at any given moment governs eligibility at that moment.
          </p>
          <p>
            By using the Services, you represent and warrant that (a) you are
            a resident of an Eligible State; and (b) at the time of any
            consent or authorization recorded under Section 25, the Subject
            of the Digital Legacy is also a resident of an Eligible State. If
            you are a Buyer commissioning a Digital Legacy on behalf of a
            Subject, both you and the Subject must independently satisfy
            these residency requirements. We reserve the right to suspend or
            terminate any account, and to refuse capture or processing of any
            Subject Content, where the residency requirements of this section
            are not satisfied.
          </p>
        </div>
      </section>

      <section id="section-2" className="scroll-mt-24 mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          2. Intellectual Property Rights
        </h2>

        <h3 className="text-xl font-semibold mb-4">Our intellectual property</h3>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            We are the owner or the licensee of all intellectual property
            rights in our Services, including all source code, databases,
            functionality, software, website designs, audio, video, text,
            photographs, and graphics in the Services (collectively, the
            "Content"), as well as the trademarks, service marks, and logos
            contained therein (the "Marks").
          </p>
          <p>
            Our Content and Marks are protected by copyright and trademark
            laws (and various other intellectual property rights and unfair
            competition laws) and treaties in the United States and around
            the world.
          </p>
          <p>
            The Content and Marks are provided in or through the Services "AS
            IS" for your personal, non-commercial use or internal business
            purpose only.
          </p>
        </div>

        <h3 className="text-xl font-semibold mt-10 mb-4">Your use of our Services</h3>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            Subject to your compliance with these Legal Terms, including the
            "Prohibited Activities" section below, we grant you a
            non-exclusive, non-transferable, revocable license to:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>access the Services; and</li>
            <li>
              download or print a copy of any portion of the Content to which
              you have properly gained access,
            </li>
          </ul>
          <p>
            solely for your personal, non-commercial use or internal business
            purpose.
          </p>
          <p>
            Except as set out in this section or elsewhere in our Legal Terms,
            no part of the Services and no Content or Marks may be copied,
            reproduced, aggregated, republished, uploaded, posted, publicly
            displayed, encoded, translated, transmitted, distributed, sold,
            licensed, or otherwise exploited for any commercial purpose
            whatsoever, without our express prior written permission.
          </p>
          <p>
            If you wish to make any use of the Services, Content, or Marks
            other than as set out in this section or elsewhere in our Legal
            Terms, please address your request to:{" "}
            <a
              href="mailto:legal@legacylensai.com"
              className="font-medium underline-offset-4 hover:underline"
            >
              legal@legacylensai.com
            </a>
            . If we ever grant you the permission to post, reproduce, or
            publicly display any part of our Services or Content, you must
            identify us as the owners or licensors of the Services, Content,
            or Marks and ensure that any copyright or proprietary notice
            appears or is visible on posting, reproducing, or displaying our
            Content.
          </p>
          <p>
            We reserve all rights not expressly granted to you in and to the
            Services, Content, and Marks.
          </p>
          <p>
            Any breach of these Intellectual Property Rights will constitute a
            material breach of our Legal Terms and your right to use our
            Services will terminate immediately.
          </p>
        </div>

        <h3 className="text-xl font-semibold mt-10 mb-4">Your submissions and contributions</h3>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            Please review this section and the "Prohibited Activities" section
            carefully prior to using our Services to understand the (a)
            rights you give us and (b) obligations you have when you post or
            upload any content through the Services.
          </p>
          <p>
            <strong>Submissions:</strong> By directly sending us any question,
            comment, suggestion, idea, feedback, or other information about
            the Services ("Submissions"), you agree to assign to us all
            intellectual property rights in such Submission. You agree that
            we shall own this Submission and be entitled to its unrestricted
            use and dissemination for any lawful purpose, commercial or
            otherwise, without acknowledgment or compensation to you.
          </p>
          <p>
            <strong>Contributions:</strong> The Services may invite you to
            chat, contribute to, or participate in blogs, message boards,
            online forums, and other functionality during which you may
            create, submit, post, display, transmit, publish, distribute, or
            broadcast content and materials to us or through the Services,
            including but not limited to text, writings, video, audio,
            photographs, music, graphics, comments, reviews, rating
            suggestions, personal information, or other material
            ("Contributions"). Any Submission that is publicly posted shall
            also be treated as a Contribution.
          </p>
          <p>
            You understand that Contributions may be viewable by other users
            of the Services and possibly through third-party websites.
          </p>
          <p>
            <strong>
              When you post Contributions, you grant us a license (including
              use of your name, trademarks, and logos):
            </strong>{" "}
            By posting any Contributions, you grant us an unrestricted,
            unlimited, irrevocable, perpetual, non-exclusive, transferable,
            royalty-free, fully-paid, worldwide right, and license to: use,
            copy, reproduce, distribute, sell, resell, publish, broadcast,
            retitle, store, publicly perform, publicly display, reformat,
            translate, excerpt (in whole or in part), and exploit your
            Contributions (including, without limitation, your image, name,
            and voice) for any purpose, commercial, advertising, or
            otherwise, to prepare derivative works of, or incorporate into
            other works, your Contributions, and to sublicense the licenses
            granted in this section. Our use and distribution may occur in
            any media formats and through any media channels.
          </p>
          <p>
            This license includes our use of your name, company name, and
            franchise name, as applicable, and any of the trademarks, service
            marks, trade names, logos, and personal and commercial images you
            provide.
          </p>
          <p>
            <strong>You are responsible for what you post or upload:</strong>{" "}
            By sending us Submissions and/or posting Contributions through
            any part of the Services or making Contributions accessible
            through the Services by linking your account through the Services
            to any of your social networking accounts, you:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              confirm that you have read and agree with our "Prohibited
              Activities" and will not post, send, publish, upload, or
              transmit through the Services any Submission nor post any
              Contribution that is illegal, harassing, hateful, harmful,
              defamatory, obscene, bullying, abusive, discriminatory,
              threatening to any person or group, sexually explicit, false,
              inaccurate, deceitful, or misleading;
            </li>
            <li>
              to the extent permissible by applicable law, waive any and all
              moral rights to any such Submission and/or Contribution;
            </li>
            <li>
              warrant that any such Submission and/or Contributions are
              original to you or that you have the necessary rights and
              licenses to submit such Submissions and/or Contributions and
              that you have full authority to grant us the above-mentioned
              rights in relation to your Submissions and/or Contributions;
              and
            </li>
            <li>
              warrant and represent that your Submissions and/or
              Contributions do not constitute confidential information.
            </li>
          </ul>
          <p>
            You are solely responsible for your Submissions and/or
            Contributions and you expressly agree to reimburse us for any and
            all losses that we may suffer because of your breach of (a) this
            section, (b) any third party's intellectual property rights, or
            (c) applicable law.
          </p>
          <p>
            <strong>We may remove or edit your Content:</strong> Although we
            have no obligation to monitor any Contributions, we shall have
            the right to remove or edit any Contributions at any time without
            notice if in our reasonable opinion we consider such
            Contributions harmful or in breach of these Legal Terms. If we
            remove or edit any such Contributions, we may also suspend or
            disable your account and report you to the authorities.
          </p>
        </div>

        <h3 className="text-xl font-semibold mt-10 mb-4">Copyright infringement</h3>
        <p className="text-lg leading-relaxed">
          We respect the intellectual property rights of others. If you
          believe that any material available on or through the Services
          infringes upon any copyright you own or control, please immediately
          refer to the "Digital Millennium Copyright Act (DMCA) Notice and
          Policy" section below.
        </p>
      </section>

      <section id="section-3" className="scroll-mt-24 mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          3. User Representations
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            By using the Services, you represent and warrant that: (1) all
            registration information you submit will be true, accurate,
            current, and complete; (2) you will maintain the accuracy of such
            information and promptly update such registration information as
            necessary; (3) you have the legal capacity and you agree to comply
            with these Legal Terms; (4) you are not a minor in the
            jurisdiction in which you reside; (5) you will not access the
            Services through automated or non-human means, whether through a
            bot, script or otherwise; (6) you will not use the Services for
            any illegal or unauthorized purpose; and (7) your use of the
            Services will not violate any applicable law or regulation.
          </p>
          <p>
            If you provide any information that is untrue, inaccurate, not
            current, or incomplete, we have the right to suspend or terminate
            your account and refuse any and all current or future use of the
            Services (or any portion thereof).
          </p>
        </div>
      </section>

      <section id="section-4" className="scroll-mt-24 mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          4. User Registration
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            You may be required to register to use the Services. You agree to
            keep your password confidential and will be responsible for all
            use of your account and password. We reserve the right to remove,
            reclaim, or change a username you select if we determine, in our
            sole discretion, that such username is inappropriate, obscene, or
            otherwise objectionable.
          </p>
        </div>
      </section>

      <section id="section-5" className="scroll-mt-24 mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          5. Purchases and Payment
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>We accept the following forms of payment:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Visa</li>
            <li>Mastercard</li>
            <li>American Express</li>
            <li>Discover</li>
          </ul>
          <p>
            You agree to provide current, complete, and accurate purchase and
            account information for all purchases made via the Services. You
            further agree to promptly update account and payment information,
            including email address, payment method, and payment card
            expiration date, so that we can complete your transactions and
            contact you as needed. Sales tax will be added to the price of
            purchases as deemed required by us. We may change prices at any
            time. All payments shall be in US dollars.
          </p>
          <p>
            You agree to pay all charges at the prices then in effect for
            your purchases and any applicable shipping fees, and you
            authorize us to charge your chosen payment provider for any such
            amounts upon placing your order. We reserve the right to correct
            any errors or mistakes in pricing, even if we have already
            requested or received payment.
          </p>
          <p>
            We reserve the right to refuse any order placed through the
            Services. We may, in our sole discretion, limit or cancel
            quantities purchased per person, per household, or per order.
            These restrictions may include orders placed by or under the same
            customer account, the same payment method, and/or orders that use
            the same billing or shipping address. We reserve the right to
            limit or prohibit orders that, in our sole judgment, appear to be
            placed by dealers, resellers, or distributors.
          </p>
        </div>
      </section>

      <section id="section-6" className="scroll-mt-24 mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          6. Prohibited Activities
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            You may not access or use the Services for any purpose other than
            that for which we make the Services available. The Services may
            not be used in connection with any commercial endeavors except
            those that are specifically endorsed or approved by us.
          </p>
          <p>As a user of the Services, you agree not to:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              Systematically retrieve data or other content from the Services
              to create or compile, directly or indirectly, a collection,
              compilation, database, or directory without written permission
              from us.
            </li>
            <li>
              Trick, defraud, or mislead us and other users, especially in any
              attempt to learn sensitive account information such as user
              passwords.
            </li>
            <li>
              Circumvent, disable, or otherwise interfere with security-related
              features of the Services, including features that prevent or
              restrict the use or copying of any Content or enforce limitations
              on the use of the Services and/or the Content contained therein.
            </li>
            <li>
              Disparage, tarnish, or otherwise harm, in our opinion, us and/or
              the Services.
            </li>
            <li>
              Use any information obtained from the Services in order to
              harass, abuse, or harm another person.
            </li>
            <li>
              Make improper use of our support services or submit false reports
              of abuse or misconduct.
            </li>
            <li>
              Use the Services in a manner inconsistent with any applicable
              laws or regulations.
            </li>
            <li>Engage in unauthorized framing of or linking to the Services.</li>
            <li>
              Upload or transmit (or attempt to upload or to transmit)
              viruses, Trojan horses, or other material, including excessive
              use of capital letters and spamming (continuous posting of
              repetitive text), that interferes with any party's
              uninterrupted use and enjoyment of the Services or modifies,
              impairs, disrupts, alters, or interferes with the use,
              features, functions, operation, or maintenance of the Services.
            </li>
            <li>
              Engage in any automated use of the system, such as using
              scripts to send comments or messages, or using any data mining,
              robots, or similar data gathering and extraction tools.
            </li>
            <li>
              Delete the copyright or other proprietary rights notice from
              any Content.
            </li>
            <li>
              Attempt to impersonate another user or person or use the
              username of another user.
            </li>
            <li>
              Upload or transmit (or attempt to upload or to transmit) any
              material that acts as a passive or active information
              collection or transmission mechanism, including without
              limitation, clear graphics interchange formats ("gifs"), 1×1
              pixels, web bugs, cookies, or other similar devices (sometimes
              referred to as "spyware" or "passive collection mechanisms" or
              "pcms").
            </li>
            <li>
              Interfere with, disrupt, or create an undue burden on the
              Services or the networks or services connected to the Services.
            </li>
            <li>
              Harass, annoy, intimidate, or threaten any of our employees or
              agents engaged in providing any portion of the Services to you.
            </li>
            <li>
              Attempt to bypass any measures of the Services designed to
              prevent or restrict access to the Services, or any portion of
              the Services.
            </li>
            <li>
              Copy or adapt the Services' software, including but not limited
              to Flash, PHP, HTML, JavaScript, or other code.
            </li>
            <li>
              Except as permitted by applicable law, decipher, decompile,
              disassemble, or reverse engineer any of the software comprising
              or in any way making up a part of the Services.
            </li>
            <li>
              Except as may be the result of standard search engine or
              Internet browser usage, use, launch, develop, or distribute any
              automated system, including without limitation, any spider,
              robot, cheat utility, scraper, or offline reader that accesses
              the Services, or use or launch any unauthorized script or other
              software.
            </li>
            <li>
              Use a buying agent or purchasing agent to make purchases on the
              Services.
            </li>
            <li>
              Make any unauthorized use of the Services, including collecting
              usernames and/or email addresses of users by electronic or other
              means for the purpose of sending unsolicited email, or creating
              user accounts by automated means or under false pretenses.
            </li>
            <li>
              Use the Services as part of any effort to compete with us or
              otherwise use the Services and/or the Content for any
              revenue-generating endeavor or commercial enterprise.
            </li>
            <li>Sell or otherwise transfer your profile.</li>
            <li>Use the Services to advertise or offer to sell goods and services.</li>
            <li>
              <strong>Misuse of Subject likeness or voice:</strong> Use, copy,
              extract, reproduce, or repurpose any Subject's voice, likeness,
              voice clone, biographical content, or other Subject Content for
              any purpose other than the authorized intent of the Digital
              Legacy as defined in our Terms of Service and the Digital
              Legacy Directive.
            </li>
            <li>
              <strong>Attempts to extract or replicate voice clone models:</strong>{" "}
              Attempt to extract, replicate, reverse-engineer, or otherwise
              obtain any voice clone model, AI model, or derived inference
              data generated by the Services, whether for the purpose of
              recreating a Subject's voice on another platform or for any
              other purpose.
            </li>
            <li>
              <strong>Adversarial or jailbreak attempts on AI avatars:</strong>{" "}
              Attempt to manipulate, jailbreak, prompt-inject, or otherwise
              circumvent the safety, dignity, or content guardrails of any AI
              avatar or interactive feature on the Services, including
              attempts to cause an avatar to produce content that conflicts
              with the Subject's authorized Digital Legacy or violates the
              dignity protections of the Digital Legacy Directive.
            </li>
            <li>
              <strong>Impersonation of Subjects, Buyers, or executors:</strong>{" "}
              Impersonate any Subject, Buyer, designated executor, partner
              institution, or LegacyLens AI representative, or attempt to
              access any Digital Legacy or account by claiming an authority
              you do not have.
            </li>
            <li>
              <strong>Unauthorized scraping or bulk content access:</strong>{" "}
              Scrape, harvest, crawl, or otherwise systematically extract
              content from the Services, including tribute pages, biographical
              content, voice recordings, or other Subject Content, except
              through interfaces and at rates explicitly authorized by
              LegacyLens.
            </li>
            <li>
              <strong>Use of Subject Content to train AI models:</strong> Use
              any Subject Content obtained from the Services to train,
              fine-tune, or otherwise inform any artificial intelligence
              model, machine learning system, or similar technology, except
              as expressly authorized by LegacyLens for the purpose of
              operating the Subject's own Digital Legacy.
            </li>
            <li>
              <strong>Misrepresenting consent or authority:</strong> Provide
              false consent records, falsify Subject identity, misrepresent
              executor authority, or otherwise provide false information to
              obtain access to, create, modify, or revoke any Digital Legacy.
            </li>
            <li>
              <strong>Content that violates Subject dignity:</strong> Create,
              post, or transmit content that depicts a Subject in a manner
              the Subject did not authorize, including content that
              misrepresents the Subject's views, places the Subject in a
              false light, or violates the dignity protections of the
              Digital Legacy Directive.
            </li>
          </ul>
        </div>
      </section>

      <section id="section-7" className="scroll-mt-24 mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          7. User Generated Contributions
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            The Services may invite you to chat, contribute to, or
            participate in blogs, message boards, online forums, and other
            functionality, and may provide you with the opportunity to
            create, submit, post, display, transmit, perform, publish,
            distribute, or broadcast content and materials to us or on the
            Services, including but not limited to text, writings, video,
            audio, photographs, graphics, comments, suggestions, or personal
            information or other material (collectively, "Contributions").
            Contributions may be viewable by other users of the Services and
            through third-party websites. As such, any Contributions you
            transmit may be treated as non-confidential and non-proprietary.
            When you create or make available any Contributions, you thereby
            represent and warrant that:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              The creation, distribution, transmission, public display, or
              performance, and the accessing, downloading, or copying of your
              Contributions do not and will not infringe the proprietary
              rights, including but not limited to the copyright, patent,
              trademark, trade secret, or moral rights of any third party.
            </li>
            <li>
              You are the creator and owner of or have the necessary
              licenses, rights, consents, releases, and permissions to use
              and to authorize us, the Services, and other users of the
              Services to use your Contributions in any manner contemplated
              by the Services and these Legal Terms.
            </li>
            <li>
              You have the written consent, release, and/or permission of
              each and every identifiable individual person in your
              Contributions to use the name or likeness of each and every
              such identifiable individual person to enable inclusion and use
              of your Contributions in any manner contemplated by the
              Services and these Legal Terms.
            </li>
            <li>Your Contributions are not false, inaccurate, or misleading.</li>
            <li>
              Your Contributions are not unsolicited or unauthorized
              advertising, promotional materials, pyramid schemes, chain
              letters, spam, mass mailings, or other forms of solicitation.
            </li>
            <li>
              Your Contributions are not obscene, lewd, lascivious, filthy,
              violent, harassing, libelous, slanderous, or otherwise
              objectionable (as determined by us).
            </li>
            <li>
              Your Contributions do not ridicule, mock, disparage, intimidate,
              or abuse anyone.
            </li>
            <li>
              Your Contributions are not used to harass or threaten (in the
              legal sense of those terms) any other person and to promote
              violence against a specific person or class of people.
            </li>
            <li>
              Your Contributions do not violate any applicable law,
              regulation, or rule.
            </li>
            <li>
              Your Contributions do not violate the privacy or publicity
              rights of any third party.
            </li>
            <li>
              Your Contributions do not violate any applicable law concerning
              child pornography, or otherwise intended to protect the health
              or well-being of minors.
            </li>
            <li>
              Your Contributions do not include any offensive comments that
              are connected to race, national origin, gender, sexual
              preference, or physical handicap.
            </li>
            <li>
              Your Contributions do not otherwise violate, or link to material
              that violates, any provision of these Legal Terms, or any
              applicable law or regulation.
            </li>
          </ul>
          <p>
            Any use of the Services in violation of the foregoing violates
            these Legal Terms and may result in, among other things,
            termination or suspension of your rights to use the Services.
          </p>
        </div>
      </section>

      <section id="section-8" className="scroll-mt-24 mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          8. Contribution License
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            By posting your Contributions to any part of the Services, you
            automatically grant, and you represent and warrant that you have
            the right to grant, to us an unrestricted, unlimited,
            irrevocable, perpetual, non-exclusive, transferable, royalty-free,
            fully-paid, worldwide right, and license to host, use, copy,
            reproduce, disclose, sell, resell, publish, broadcast, retitle,
            archive, store, cache, publicly perform, publicly display,
            reformat, translate, transmit, excerpt (in whole or in part), and
            distribute such Contributions (including, without limitation,
            your image and voice) for any purpose, commercial, advertising,
            or otherwise, and to prepare derivative works of, or incorporate
            into other works, such Contributions, and grant and authorize
            sublicenses of the foregoing. The use and distribution may occur
            in any media formats and through any media channels.
          </p>
          <p>
            This license will apply to any form, media, or technology now
            known or hereafter developed, and includes our use of your name,
            company name, and franchise name, as applicable, and any of the
            trademarks, service marks, trade names, logos, and personal and
            commercial images you provide. You waive all moral rights in your
            Contributions, and you warrant that moral rights have not
            otherwise been asserted in your Contributions.
          </p>
          <p>
            We do not assert any ownership over your Contributions. You
            retain full ownership of all of your Contributions and any
            intellectual property rights or other proprietary rights
            associated with your Contributions. We are not liable for any
            statements or representations in your Contributions provided by
            you in any area on the Services. You are solely responsible for
            your Contributions to the Services and you expressly agree to
            exonerate us from any and all responsibility and to refrain from
            any legal action against us regarding your Contributions.
          </p>
          <p>
            We have the right, in our sole and absolute discretion, (1) to
            edit, redact, or otherwise change any Contributions; (2) to
            re-categorize any Contributions to place them in more
            appropriate locations on the Services; and (3) to pre-screen or
            delete any Contributions at any time and for any reason, without
            notice. We have no obligation to monitor your Contributions.
          </p>
        </div>
      </section>

      <section id="section-9" className="scroll-mt-24 mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          9. Third-Party Websites and Content
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            The Services may contain (or you may be sent via the Site) links
            to other websites ("Third-Party Websites") as well as articles,
            photographs, text, graphics, pictures, designs, music, sound,
            video, information, applications, software, and other content or
            items belonging to or originating from third parties ("Third-Party
            Content"). Such Third-Party Websites and Third-Party Content are
            not investigated, monitored, or checked for accuracy,
            appropriateness, or completeness by us, and we are not
            responsible for any Third-Party Websites accessed through the
            Services or any Third-Party Content posted on, available through,
            or installed from the Services, including the content, accuracy,
            offensiveness, opinions, reliability, privacy practices, or other
            policies of or contained in the Third-Party Websites or the
            Third-Party Content. Inclusion of, linking to, or permitting the
            use or installation of any Third-Party Websites or any
            Third-Party Content does not imply approval or endorsement
            thereof by us. If you decide to leave the Services and access the
            Third-Party Websites or to use or install any Third-Party
            Content, you do so at your own risk, and you should be aware
            these Legal Terms no longer govern. You should review the
            applicable terms and policies, including privacy and data
            gathering practices, of any website to which you navigate from
            the Services or relating to any applications you use or install
            from the Services. Any purchases you make through Third-Party
            Websites will be through other websites and from other companies,
            and we take no responsibility whatsoever in relation to such
            purchases which are exclusively between you and the applicable
            third party. You agree and acknowledge that we do not endorse the
            products or services offered on Third-Party Websites and you
            shall hold us blameless from any harm caused by your purchase of
            such products or services. Additionally, you shall hold us
            blameless from any losses sustained by you or harm caused to you
            relating to or resulting in any way from any Third-Party Content
            or any contact with Third-Party Websites.
          </p>
        </div>
      </section>

      <section id="section-10" className="scroll-mt-24 mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          10. Services Management
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            We reserve the right, but not the obligation, to: (1) monitor the
            Services for violations of these Legal Terms; (2) take
            appropriate legal action against anyone who, in our sole
            discretion, violates the law or these Legal Terms, including
            without limitation, reporting such user to law enforcement
            authorities; (3) in our sole discretion and without limitation,
            refuse, restrict access to, limit the availability of, or disable
            (to the extent technologically feasible) any of your
            Contributions or any portion thereof; (4) in our sole discretion
            and without limitation, notice, or liability, to remove from the
            Services or otherwise disable all files and content that are
            excessive in size or are in any way burdensome to our systems;
            and (5) otherwise manage the Services in a manner designed to
            protect our rights and property and to facilitate the proper
            functioning of the Services.
          </p>
        </div>
      </section>

      <section id="section-11" className="scroll-mt-24 mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          11. Privacy Policy
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            We care about data privacy and security. Please review our{" "}
            <a
              href="/privacy"
              className="font-medium underline-offset-4 hover:underline"
            >
              Privacy Policy
            </a>
            . By using the Services, you agree to be bound by our Privacy
            Policy, which is incorporated into these Legal Terms. Please be
            advised the Services are hosted in the United States. If you
            access the Services from any other region of the world with laws
            or other requirements governing personal data collection, use, or
            disclosure that differ from applicable laws in the United States,
            then through your continued use of the Services, you are
            transferring your data to the United States, and you expressly
            consent to have your data transferred to and processed in the
            United States.
          </p>
        </div>
      </section>

      <section id="section-12" className="scroll-mt-24 mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          12. Digital Millennium Copyright Act (DMCA) Notice and Policy
        </h2>

        <h3 className="text-xl font-semibold mb-4">Notifications</h3>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            We respect the intellectual property rights of others. If you
            believe that any material available on or through the Services
            infringes upon any copyright you own or control, please
            immediately notify our Designated Copyright Agent using the
            contact information provided below (a "Notification"). A copy of
            your Notification will be sent to the person who posted or
            stored the material addressed in the Notification. Please be
            advised that pursuant to federal law you may be held liable for
            damages if you make material misrepresentations in a
            Notification. Thus, if you are not sure that material located on
            or linked to by the Services infringes your copyright, you
            should consider first contacting an attorney.
          </p>
          <p>
            All Notifications should meet the requirements of DMCA 17 U.S.C.
            § 512(c)(3) and include the following information: (1) A
            physical or electronic signature of a person authorized to act
            on behalf of the owner of an exclusive right that is allegedly
            infringed; (2) identification of the copyrighted work claimed to
            have been infringed, or, if multiple copyrighted works on the
            Services are covered by the Notification, a representative list
            of such works on the Services; (3) identification of the
            material that is claimed to be infringing or to be the subject
            of infringing activity and that is to be removed or access to
            which is to be disabled, and information reasonably sufficient
            to permit us to locate the material; (4) information reasonably
            sufficient to permit us to contact the complaining party, such
            as an address, telephone number, and, if available, an email
            address at which the complaining party may be contacted; (5) a
            statement that the complaining party has a good faith belief
            that use of the material in the manner complained of is not
            authorized by the copyright owner, its agent, or the law; and
            (6) a statement that the information in the notification is
            accurate, and under penalty of perjury, that the complaining
            party is authorized to act on behalf of the owner of an
            exclusive right that is allegedly infringed upon.
          </p>
        </div>

        <h3 className="text-xl font-semibold mt-10 mb-4">Counter Notification</h3>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            If you believe your own copyrighted material has been removed
            from the Services as a result of a mistake or misidentification,
            you may submit a written counter notification to our Designated
            Copyright Agent using the contact information provided below (a
            "Counter Notification"). To be an effective Counter Notification
            under the DMCA, your Counter Notification must include
            substantially the following: (1) identification of the material
            that has been removed or disabled and the location at which the
            material appeared before it was removed or disabled; (2) a
            statement that you consent to the jurisdiction of the Federal
            District Court in which your address is located, or if your
            address is outside the United States, for any judicial district
            in which we are located; (3) a statement that you will accept
            service of process from the party that filed the Notification or
            the party's agent; (4) your name, address, and telephone number;
            (5) a statement under penalty of perjury that you have a good
            faith belief that the material in question was removed or
            disabled as a result of a mistake or misidentification of the
            material to be removed or disabled; and (6) your physical or
            electronic signature.
          </p>
          <p>
            If you send us a valid, written Counter Notification meeting the
            requirements described above, we will restore your removed or
            disabled material, unless we first receive notice from the party
            filing the Notification informing us that such party has filed a
            court action to restrain you from engaging in infringing activity
            related to the material in question. Please note that if you
            materially misrepresent that the disabled or removed content was
            removed by mistake or misidentification, you may be liable for
            damages, including costs and attorney's fees. Filing a false
            Counter Notification constitutes perjury.
          </p>
        </div>

        <h3 className="text-xl font-semibold mt-10 mb-4">Designated Copyright Agent</h3>
        <address className="not-italic text-lg leading-relaxed">
          <div>Syrus Sipperly, Founder</div>
          <div>Attn: Copyright Agent</div>
          <div>447 Broadway, 2nd Floor #1240</div>
          <div>New York, NY 10013</div>
          <div>United States</div>
          <div>
            <a
              href="mailto:dmca@legacylensai.com"
              className="font-medium underline-offset-4 hover:underline"
            >
              dmca@legacylensai.com
            </a>
          </div>
        </address>
      </section>

      <section id="section-13" className="scroll-mt-24 mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          13. Term and Termination
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            These Legal Terms shall remain in full force and effect while you
            use the Services. WITHOUT LIMITING ANY OTHER PROVISION OF THESE
            LEGAL TERMS, WE RESERVE THE RIGHT TO, IN OUR SOLE DISCRETION AND
            WITHOUT NOTICE OR LIABILITY, DENY ACCESS TO AND USE OF THE
            SERVICES (INCLUDING BLOCKING CERTAIN IP ADDRESSES), TO ANY PERSON
            FOR ANY REASON OR FOR NO REASON, INCLUDING WITHOUT LIMITATION FOR
            BREACH OF ANY REPRESENTATION, WARRANTY, OR COVENANT CONTAINED IN
            THESE LEGAL TERMS OR OF ANY APPLICABLE LAW OR REGULATION. WE MAY
            TERMINATE YOUR USE OR PARTICIPATION IN THE SERVICES OR DELETE
            YOUR ACCOUNT AND ANY CONTENT OR INFORMATION THAT YOU POSTED AT
            ANY TIME, WITHOUT WARNING, IN OUR SOLE DISCRETION.
          </p>
          <p>
            If we terminate or suspend your account for any reason, you are
            prohibited from registering and creating a new account under your
            name, a fake or borrowed name, or the name of any third party,
            even if you may be acting on behalf of the third party. In
            addition to terminating or suspending your account, we reserve
            the right to take appropriate legal action, including without
            limitation pursuing civil, criminal, and injunctive redress.
          </p>
        </div>
      </section>

      <section id="section-14" className="scroll-mt-24 mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          14. Modifications and Interruptions
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            We reserve the right to change, modify, or remove the contents of
            the Services at any time or for any reason at our sole discretion
            without notice. However, we have no obligation to update any
            information on our Services. We will not be liable to you or any
            third party for any modification, price change, suspension, or
            discontinuance of the Services.
          </p>
          <p>
            We cannot guarantee the Services will be available at all times.
            We may experience hardware, software, or other problems or need
            to perform maintenance related to the Services, resulting in
            interruptions, delays, or errors. We reserve the right to change,
            revise, update, suspend, discontinue, or otherwise modify the
            Services at any time or for any reason without notice to you. You
            agree that we have no liability whatsoever for any loss, damage,
            or inconvenience caused by your inability to access or use the
            Services during any downtime or discontinuance of the Services.
            Nothing in these Legal Terms will be construed to obligate us to
            maintain and support the Services or to supply any corrections,
            updates, or releases in connection therewith.
          </p>
        </div>
      </section>

      <section id="section-15" className="scroll-mt-24 mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          15. Governing Law
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            These Legal Terms and your use of the Services are governed by
            and construed in accordance with the laws of the State of
            Delaware applicable to agreements made and to be entirely
            performed within the State of Delaware, without regard to its
            conflict of law principles.
          </p>
        </div>
      </section>

      <section id="section-16" className="scroll-mt-24 mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          16. Dispute Resolution
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            Any legal action of whatever nature brought by either you or us
            (collectively, the "Parties" and individually, a "Party") shall
            be commenced or prosecuted in the state and federal courts
            located in New York County, New York, and the Parties hereby
            consent to, and waive all defenses of lack of personal
            jurisdiction and forum non conveniens with respect to venue and
            jurisdiction in such state and federal courts. Application of
            the United Nations Convention on Contracts for the International
            Sale of Goods and the Uniform Computer Information Transaction
            Act (UCITA) are excluded from these Legal Terms. In no event
            shall any claim, action, or proceeding brought by either Party
            related in any way to the Services be commenced more than one
            (1) year after the cause of action arose.
          </p>
        </div>
      </section>

      <section id="section-17" className="scroll-mt-24 mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          17. Corrections
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            There may be information on the Services that contains
            typographical errors, inaccuracies, or omissions, including
            descriptions, pricing, availability, and various other
            information. We reserve the right to correct any errors,
            inaccuracies, or omissions and to change or update the
            information on the Services at any time, without prior notice.
          </p>
        </div>
      </section>

      <section id="section-18" className="scroll-mt-24 mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          18. Disclaimer
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            THE SERVICES ARE PROVIDED ON AN AS-IS AND AS-AVAILABLE BASIS. YOU
            AGREE THAT YOUR USE OF THE SERVICES WILL BE AT YOUR SOLE RISK. TO
            THE FULLEST EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES,
            EXPRESS OR IMPLIED, IN CONNECTION WITH THE SERVICES AND YOUR USE
            THEREOF, INCLUDING, WITHOUT LIMITATION, THE IMPLIED WARRANTIES OF
            MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND
            NON-INFRINGEMENT. WE MAKE NO WARRANTIES OR REPRESENTATIONS ABOUT
            THE ACCURACY OR COMPLETENESS OF THE SERVICES' CONTENT OR THE
            CONTENT OF ANY WEBSITES OR MOBILE APPLICATIONS LINKED TO THE
            SERVICES AND WE WILL ASSUME NO LIABILITY OR RESPONSIBILITY FOR
            ANY (1) ERRORS, MISTAKES, OR INACCURACIES OF CONTENT AND
            MATERIALS, (2) PERSONAL INJURY OR PROPERTY DAMAGE, OF ANY NATURE
            WHATSOEVER, RESULTING FROM YOUR ACCESS TO AND USE OF THE
            SERVICES, (3) ANY UNAUTHORIZED ACCESS TO OR USE OF OUR SECURE
            SERVERS AND/OR ANY AND ALL PERSONAL INFORMATION AND/OR FINANCIAL
            INFORMATION STORED THEREIN, (4) ANY INTERRUPTION OR CESSATION OF
            TRANSMISSION TO OR FROM THE SERVICES, (5) ANY BUGS, VIRUSES,
            TROJAN HORSES, OR THE LIKE WHICH MAY BE TRANSMITTED TO OR THROUGH
            THE SERVICES BY ANY THIRD PARTY, AND/OR (6) ANY ERRORS OR
            OMISSIONS IN ANY CONTENT AND MATERIALS OR FOR ANY LOSS OR DAMAGE
            OF ANY KIND INCURRED AS A RESULT OF THE USE OF ANY CONTENT
            POSTED, TRANSMITTED, OR OTHERWISE MADE AVAILABLE VIA THE
            SERVICES. WE DO NOT WARRANT, ENDORSE, GUARANTEE, OR ASSUME
            RESPONSIBILITY FOR ANY PRODUCT OR SERVICE ADVERTISED OR OFFERED
            BY A THIRD PARTY THROUGH THE SERVICES, ANY HYPERLINKED WEBSITE,
            OR ANY WEBSITE OR MOBILE APPLICATION FEATURED IN ANY BANNER OR
            OTHER ADVERTISING, AND WE WILL NOT BE A PARTY TO OR IN ANY WAY BE
            RESPONSIBLE FOR MONITORING ANY TRANSACTION BETWEEN YOU AND ANY
            THIRD-PARTY PROVIDERS OF PRODUCTS OR SERVICES. AS WITH THE
            PURCHASE OF A PRODUCT OR SERVICE THROUGH ANY MEDIUM OR IN ANY
            ENVIRONMENT, YOU SHOULD USE YOUR BEST JUDGMENT AND EXERCISE
            CAUTION WHERE APPROPRIATE.
          </p>
        </div>
      </section>

      <section id="section-19" className="scroll-mt-24 mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          19. Limitations of Liability
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            IN NO EVENT WILL WE OR OUR DIRECTORS, EMPLOYEES, OR AGENTS BE
            LIABLE TO YOU OR ANY THIRD PARTY FOR ANY DIRECT, INDIRECT,
            CONSEQUENTIAL, EXEMPLARY, INCIDENTAL, SPECIAL, OR PUNITIVE
            DAMAGES, INCLUDING LOST PROFIT, LOST REVENUE, LOSS OF DATA, OR
            OTHER DAMAGES ARISING FROM YOUR USE OF THE SERVICES, EVEN IF WE
            HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
            NOTWITHSTANDING ANYTHING TO THE CONTRARY CONTAINED HEREIN, OUR
            LIABILITY TO YOU FOR ANY CAUSE WHATSOEVER AND REGARDLESS OF THE
            FORM OF THE ACTION, WILL AT ALL TIMES BE LIMITED TO THE AMOUNT
            PAID, IF ANY, BY YOU TO US DURING THE TWELVE (12) MONTH PERIOD
            PRIOR TO ANY CAUSE OF ACTION ARISING. CERTAIN US STATE LAWS AND
            INTERNATIONAL LAWS DO NOT ALLOW LIMITATIONS ON IMPLIED WARRANTIES
            OR THE EXCLUSION OR LIMITATION OF CERTAIN DAMAGES. IF THESE LAWS
            APPLY TO YOU, SOME OR ALL OF THE ABOVE DISCLAIMERS OR LIMITATIONS
            MAY NOT APPLY TO YOU, AND YOU MAY HAVE ADDITIONAL RIGHTS.
          </p>
        </div>
      </section>

      <section id="section-20" className="scroll-mt-24 mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          20. Indemnification
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            You agree to defend, indemnify, and hold us harmless, including
            our subsidiaries, affiliates, and all of our respective officers,
            agents, partners, and employees, from and against any loss,
            damage, liability, claim, or demand, including reasonable
            attorneys' fees and expenses, made by any third party due to or
            arising out of: (1) your Contributions; (2) use of the Services;
            (3) breach of these Legal Terms; (4) any breach of your
            representations and warranties set forth in these Legal Terms;
            (5) your violation of the rights of a third party, including but
            not limited to intellectual property rights; or (6) any overt
            harmful act toward any other user of the Services with whom you
            connected via the Services. Notwithstanding the foregoing, we
            reserve the right, at your expense, to assume the exclusive
            defense and control of any matter for which you are required to
            indemnify us, and you agree to cooperate, at your expense, with
            our defense of such claims. We will use reasonable efforts to
            notify you of any such claim, action, or proceeding which is
            subject to this indemnification upon becoming aware of it.
          </p>
        </div>
      </section>

      <section id="section-21" className="scroll-mt-24 mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          21. User Data
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            We will maintain certain data that you transmit to the Services
            for the purpose of managing the performance of the Services, as
            well as data relating to your use of the Services. Although we
            perform regular routine backups of data, you are solely
            responsible for all data that you transmit or that relates to
            any activity you have undertaken using the Services. You agree
            that we shall have no liability to you for any loss or
            corruption of any such data, and you hereby waive any right of
            action against us arising from any such loss or corruption of
            such data.
          </p>
        </div>
      </section>

      <section id="section-22" className="scroll-mt-24 mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          22. Electronic Communications, Transactions, and Signatures
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            Visiting the Services, sending us emails, and completing online
            forms constitute electronic communications. You consent to
            receive electronic communications, and you agree that all
            agreements, notices, disclosures, and other communications we
            provide to you electronically, via email and on the Services,
            satisfy any legal requirement that such communication be in
            writing. YOU HEREBY AGREE TO THE USE OF ELECTRONIC SIGNATURES,
            CONTRACTS, ORDERS, AND OTHER RECORDS, AND TO ELECTRONIC DELIVERY
            OF NOTICES, POLICIES, AND RECORDS OF TRANSACTIONS INITIATED OR
            COMPLETED BY US OR VIA THE SERVICES. You hereby waive any rights
            or requirements under any statutes, regulations, rules,
            ordinances, or other laws in any jurisdiction which require an
            original signature or delivery or retention of non-electronic
            records, or to payments or the granting of credits by any means
            other than electronic means.
          </p>
        </div>
      </section>

      <section id="section-23" className="scroll-mt-24 mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          23. California Users and Residents
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            If any complaint with us is not satisfactorily resolved, you can
            contact the Complaint Assistance Unit of the Division of Consumer
            Services of the California Department of Consumer Affairs in
            writing at 1625 North Market Blvd., Suite N 112, Sacramento,
            California 95834 or by telephone at (800) 952-5210 or (916)
            445-1254.
          </p>
        </div>
      </section>

      <section id="section-24" className="scroll-mt-24 mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          24. Miscellaneous
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            These Legal Terms and any policies or operating rules posted by
            us on the Services or in respect to the Services constitute the
            entire agreement and understanding between you and us. Our
            failure to exercise or enforce any right or provision of these
            Legal Terms shall not operate as a waiver of such right or
            provision. These Legal Terms operate to the fullest extent
            permissible by law. We may assign any or all of our rights and
            obligations to others at any time. We shall not be responsible
            or liable for any loss, damage, delay, or failure to act caused
            by any cause beyond our reasonable control. If any provision or
            part of a provision of these Legal Terms is determined to be
            unlawful, void, or unenforceable, that provision or part of the
            provision is deemed severable from these Legal Terms and does
            not affect the validity and enforceability of any remaining
            provisions. There is no joint venture, partnership, employment
            or agency relationship created between you and us as a result
            of these Legal Terms or use of the Services. You agree that
            these Legal Terms will not be construed against us by virtue of
            having drafted them. You hereby waive any and all defenses you
            may have based on the electronic form of these Legal Terms and
            the lack of signing by the parties hereto to execute these Legal
            Terms.
          </p>
        </div>
      </section>

      <section id="section-25" className="scroll-mt-24 mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          25. Subject Consent and Authorization
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            <strong>25.1 Subject Authorization Required.</strong> No Digital
            Legacy may be created on the LegacyLens platform without the
            personal authorization of the Subject. The Subject must be at
            least eighteen (18) years of age and a resident of an Eligible
            State (as defined in Section 1) at the time of authorization. A
            Buyer may commission and pay for the creation of a Digital
            Legacy as a gift to the Subject or to the Subject's family, but
            the Subject themselves must complete the consent process
            described in this Section before any voice, likeness,
            biographical content, or personal recording is captured,
            processed, or retained by LegacyLens. No third party — including
            a Subject's family member, spouse, child, employer, or legal
            representative acting under power of attorney — may consent to
            the creation of a Digital Legacy on the Subject's behalf during
            the Subject's lifetime, except as provided in Section 25.6 below
            for revocation by designated executors after death.
          </p>
          <p>
            <strong>25.2 Comprehension.</strong> Before consent is recorded,
            the Subject must be presented with, and acknowledge, a
            plain-language explanation of: (a) what is being created (voice
            recording, likeness capture, biographical content, voice clone,
            or any combination thereof); (b) who will have access to the
            Digital Legacy and under what conditions; (c) what may happen to
            the Digital Legacy during the Subject's life and after the
            Subject's death; (d) the Subject's rights to revoke consent
            under Section 25.5; and (e) the version of the Digital Legacy
            Directive in effect at the time of authorization. LegacyLens
            will not record consent until the Subject has acknowledged each
            of these items.
          </p>
          <p>
            <strong>25.3 Verifiable Consent Record.</strong> Every consent
            authorization recorded under this Section will include an audio
            or video recording of the Subject reading aloud a consent
            statement that identifies the Subject by name and states the
            scope of authorization granted. This recording is
            cryptographically bound to the moment of authorization and
            stored as part of the Subject's permanent consent record. The
            biometric characteristics of the Subject's voice and likeness
            in this recording are authenticated against the voice and
            likeness used in the Digital Legacy itself, providing a
            verifiable record that the consent was given by the Subject.
          </p>
          <p>
            <strong>25.4 Scope of Authorization.</strong> The scope of what
            is authorized is defined in the consent statement the Subject
            reads at the time of recording. Scope includes, at a minimum:
            (a) what likeness elements may be captured (voice, image, video,
            3D scan); (b) what content categories may be recorded
            (biographical narrative, personal stories, opinions, statements
            of intent); (c) the duration of authorization (typically
            perpetual, subject to revocation); and (d) the audience to whom
            the Digital Legacy may be made accessible (typically public, but
            may be restricted at the Subject's election). LegacyLens will
            not expand the scope of authorization beyond what was
            authorized at the time of consent without obtaining a new
            consent recording from the Subject that explicitly authorizes
            the expanded scope.
          </p>
          <p>
            <strong>25.5 Revocation During Lifetime.</strong> A Subject may
            revoke consent for their Digital Legacy at any time during the
            Subject's lifetime, in whole or in part, by submitting a
            revocation request through the LegacyLens platform or by
            contacting LegacyLens at the contact address specified in
            Section 28. Upon receipt of a valid revocation request from a
            verified Subject, LegacyLens will: (a) immediately suspend
            public access to the Digital Legacy; (b) within thirty (30)
            days, permanently remove the revoked content from the
            LegacyLens platform, except for the consent record itself,
            which LegacyLens may retain in suspended state for a period of
            up to ten (10) years for audit and legal purposes, consistent
            with the legal-hold immutability of LegacyLens's compliance
            backup systems; and (c) notify any partner institutions hosting
            the Digital Legacy that access has been suspended. Revocation
            does not entitle the Subject or any Buyer to a refund of fees
            previously paid, except as may be required by applicable law.
          </p>
          <p>
            <strong>25.6 Action After Death.</strong> After the death of a
            Subject, designated executors of the Subject's estate may act
            on the Subject's previously stated wishes, but only within the
            scope of authorization the Subject granted during life. An
            executor may not expand the scope of a Digital Legacy beyond
            what the Subject originally authorized. An executor may revoke
            a Digital Legacy if doing so is consistent with the Subject's
            documented wishes; LegacyLens may require the executor to
            provide reasonable evidence of authority and of the Subject's
            wishes before acting on a posthumous revocation.
          </p>
          <p>
            <strong>25.7 Directive Version Lock.</strong> Each consent
            record stores the version of the Digital Legacy Directive in
            effect at the moment of authorization. If the Directive is
            revised after a consent record is created, the original consent
            remains governed by the version of the Directive the Subject
            agreed to. The Subject (or, after death, the Subject's
            executor) retains the right to re-consent under a newer version
            of the Directive, or to revoke consent if a newer version no
            longer reflects the Subject's wishes.
          </p>
        </div>
      </section>

      <section id="section-26" className="scroll-mt-24 mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          26. Ownership, Use, and No Commercial Exploitation
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            <strong>26.1 Ownership.</strong> All voice recordings, likeness
            captures, biographical content, voice clones, 3D scans, and
            other material captured from a Subject under this Agreement
            (collectively, "Subject Content") remain the property of the
            Subject during the Subject's lifetime, and pass to the Subject's
            estate upon the Subject's death. LegacyLens AI, Inc. holds
            Subject Content in trust on behalf of the Subject and the
            Subject's estate; LegacyLens does not own Subject Content and
            does not acquire any ownership interest in Subject Content by
            virtue of capturing, processing, storing, or making it
            accessible.
          </p>
          <p>
            <strong>26.2 License to LegacyLens.</strong> The Subject grants
            LegacyLens a limited, non-exclusive, royalty-free license to
            use Subject Content solely for the purposes of: (a) creating,
            hosting, and providing access to the Subject's Digital Legacy
            in accordance with the scope of authorization granted in
            Section 25.4; (b) preserving Subject Content under Section 27
            and applicable preservation commitments; (c) responding to
            lawful legal process; and (d) any other purpose for which the
            Subject has granted explicit, separate authorization. This
            license is co-terminus with the Subject's consent and
            terminates immediately upon revocation under Section 25.5 or
            25.6.
          </p>
          <p>
            <strong>26.3 Prohibited Uses.</strong> LegacyLens will not, and
            will not permit any third party to: (a) use Subject Content,
            in whole or in part, to train, fine-tune, or otherwise improve
            any commercial artificial intelligence model, whether
            developed by LegacyLens or by a third party, except for AI
            models used solely to generate the Subject's own Digital
            Legacy as authorized by the Subject; (b) use Subject Content
            for advertising, marketing, or promotional purposes outside
            the Digital Legacy itself, including but not limited to using
            a Subject's voice or likeness to endorse products or services;
            (c) license, sell, transfer, or otherwise convey Subject
            Content to any third party, except as part of an acquisition
            governed by Section 27.4 below; (d) use Subject Content for
            any purpose beyond honoring the Subject and fulfilling the
            authorized intent of the Digital Legacy.
          </p>
          <p>
            <strong>26.4 Permitted Fees.</strong> Notwithstanding Section
            26.3, LegacyLens may charge access fees, subscription fees, or
            other modest fees in connection with Subject Content where
            those fees: (a) fund the preservation of Subject Content under
            Section 27; (b) compensate stakeholders, including the Buyer
            who commissioned the Digital Legacy and partner institutions
            hosting the tribute; (c) sustain operations of LegacyLens; or
            (d) fund related charitable causes. No fee charged under this
            Section 26.4 may be construed as monetizing Subject Content
            for purposes outside the authorized intent of the Digital
            Legacy.
          </p>
          <p>
            <strong>26.5 Statutory Rights of Publicity.</strong> Nothing in
            this Agreement waives, modifies, or limits any rights of
            publicity, post-mortem rights, or other statutory protections
            that the Subject (or, after the Subject's death, the Subject's
            estate or heirs) may hold under the laws of the Subject's
            state or country of residence, including but not limited to
            California Civil Code § 3344.1, the Tennessee ELVIS Act, New
            York Civil Rights Law § 50-f, and analogous statutes in other
            jurisdictions. Where such statutes provide protections greater
            than those provided by this Agreement, those statutes control.
          </p>
        </div>
      </section>

      <section id="section-27" className="scroll-mt-24 mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          27. Portability and Platform Transition
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            <strong>27.1 Preservation Commitment.</strong> LegacyLens
            commits to preserving Subject Content and consent records in
            perpetuity, subject to the terms of this Section 27 and to any
            revocation by the Subject under Section 25.5 or 25.6.
            Preservation will be implemented with architectural redundancy
            across independent storage systems, in accordance with the
            preservation architecture published on the LegacyLens website,
            as updated from time to time.
          </p>
          <p>
            <strong>27.2 Notice of Cessation of Operations.</strong> If
            LegacyLens AI, Inc. determines that it will cease operations
            — whether by voluntary wind-down, dissolution, bankruptcy, or
            any other cause — LegacyLens will provide written notice to:
            (a) every Subject (or, for deceased Subjects, the Subject's
            designated executor or, if none, the Buyer who commissioned
            the Digital Legacy) at the most recent contact information on
            file; and (b) the public, by prominent notice on the
            LegacyLens website. Notice will be provided no fewer than one
            hundred eighty (180) days before any Subject Content or
            consent records are permanently removed from LegacyLens
            systems.
          </p>
          <p>
            <strong>27.3 Retrieval Window.</strong> Following notice under
            Section 27.2, LegacyLens will provide every Subject (or
            executor or Buyer, as applicable) a window of no fewer than
            three hundred sixty-five (365) days from the date of notice
            during which they may retrieve their Digital Legacy in a
            portable, usable format. The portable format will include, at
            minimum: (a) all original recordings and likeness captures of
            the Subject; (b) the full consent record, including the
            verifiable consent recording described in Section 25.3; (c)
            any voice clone or other derived content created from the
            Subject's likeness, in a format that allows the Subject or
            estate to use the content independent of the LegacyLens
            platform; and (d) metadata sufficient to reconstruct the
            structure and provenance of the Digital Legacy. LegacyLens
            will not charge a fee for retrieval under this Section 27.3.
          </p>
          <p>
            <strong>27.4 Acquisition.</strong> If LegacyLens AI, Inc. is
            acquired by, merges with, or transfers substantially all of
            its assets to another entity (the "Acquirer"), the Acquirer
            must, as a condition of the transaction: (a) publicly commit,
            in writing and posted on the Acquirer's website, to honoring
            the Digital Legacy Directive (in the version in effect at the
            time of acquisition or any subsequent version that maintains
            substantively equivalent protections); OR (b) provide every
            Subject the same notice and retrieval guarantees specified in
            Sections 27.2 and 27.3 of this Agreement, treating the date
            of acquisition as the date of notice. The Acquirer's election
            of (a) or (b) must be communicated to every Subject within
            thirty (30) days of the closing of the acquisition. This
            Section 27.4 survives any acquisition or transfer of
            LegacyLens AI, Inc., and the obligations herein bind any
            Acquirer regardless of whether the Acquirer expressly assents
            to them, by virtue of the contractual commitments LegacyLens
            has made to its Subjects under this Agreement.
          </p>
          <p>
            <strong>27.5 Perpetual Preservation Trust.</strong> LegacyLens
            is establishing a perpetual preservation trust, modeled on the
            legally regulated perpetual care funds that sustain physical
            cemeteries, intended to ensure that tributes commissioned
            through the LegacyLens platform remain preserved even in the
            event of LegacyLens's dissolution. LegacyLens commits to
            formally establishing the trust within twenty-four (24)
            months of the date of these Legal Terms, and to publishing
            details of the trust structure, including the funding
            mechanism and the fiduciary entity administering the trust,
            on the LegacyLens website upon establishment. Pending formal
            establishment of the trust, LegacyLens maintains a segregated
            preservation reserve in a dedicated investment account held
            in the name of LegacyLens AI, Inc., funded by allocations
            from Digital Legacy sales, the existence and funding
            methodology of which are disclosed on the LegacyLens website.
            Establishment of the trust does not relieve LegacyLens of its
            obligations under this Section 27; the trust is supplementary
            to, not in lieu of, the notice and retrieval guarantees in
            Sections 27.2 and 27.3.
          </p>
          <p>
            <strong>27.6 Survival.</strong> The obligations in this
            Section 27 survive termination of this Agreement, dissolution
            of LegacyLens AI, Inc., and any acquisition or transfer
            governed by Section 27.4. A Subject's right to notice and
            retrieval under this Section 27 cannot be waived in a
            separate agreement or modified except by a subsequent written
            agreement signed by the Subject in which the Subject
            expressly waives or modifies the rights set forth in this
            Section 27 with full knowledge of the rights being waived or
            modified.
          </p>
          <p>
            The full text of the Digital Legacy Directive, including its
            current version and all prior versions, is available at{" "}
            <a
              href="/digital-legacy-directive"
              className="font-medium underline-offset-4 hover:underline"
            >
              legacylensai.com/digital-legacy-directive
            </a>
            . Where any provision of these Terms of Service conflicts with
            the Digital Legacy Directive, the document providing greater
            protection to the Subject controls.
          </p>
        </div>
      </section>

      <section id="section-28" className="scroll-mt-24 mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          28. Contact Us
        </h2>
        <p className="text-lg leading-relaxed mb-5">
          In order to resolve a complaint regarding the Services or to
          receive further information regarding use of the Services, please
          contact us at:
        </p>
        <address className="not-italic font-semibold text-lg leading-relaxed">
          <div>LegacyLens AI, Inc.</div>
          <div>447 Broadway, 2nd Floor #1240</div>
          <div>New York, NY 10013</div>
          <div>United States</div>
          <div>Phone: (+1) 646-389-1263</div>
          <div>
            <a
              href="mailto:legal@legacylensai.com"
              className="font-medium underline-offset-4 hover:underline"
            >
              legal@legacylensai.com
            </a>
          </div>
        </address>
      </section>
    </main>
  );
}
